import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Button,
  Box,
  Stack,
  Text,
  Divider,
  Input,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Slider,
} from '@ltvco/refresh-lib/theme';
import { nameize, useStatesOptionsAbbr } from '@ltvco/refresh-lib/utils';
import {
  RapidsInput,
  RapidsSearchData,
  contactSeniorityLevels,
  contactIndustries,
  contactEmployeeCountRanges,
} from '@ltvco/refresh-lib/v1';
import { AdvancedSearchURLParams, CollectionType } from '@ltvco/refresh-lib/v2';
import { Close, Tune } from '@mui/icons-material';
import { SetStateAction, Dispatch, useContext } from 'react';

import {
  ReportColumnContainer,
  AgeInput,
  SourceSelect,
} from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  FILTERS_BY_COLLECTION_TYPE,
} from 'components/ChatBV/constants';
import { monthOptions } from 'routes/SearchPerson/SearchFilter/constants';

type FiltersColumnProps = {
  currentWidth: string;
  isMobileBreakpoint: boolean;
  onClose: () => void;
  searchParams: AdvancedSearchURLParams;
  setSearchParams: Dispatch<SetStateAction<AdvancedSearchURLParams>>;
  collectionType: CollectionType;
  setCollectionType: (collectionType: CollectionType) => void;
};

const RapidsInputStyle = {
  '& > .MuiAutocomplete-root': {
    width: '100%',
    '& > .address-input-container': {
      '& > .address-input': {
        width: '100%',
        '& > .rapids-search-icon': {
          display: 'none',
        },
      },
    },
  },
};

export const FiltersColumn = ({
  currentWidth,
  isMobileBreakpoint,
  onClose,
  searchParams,
  setSearchParams,
  collectionType,
  setCollectionType,
}: FiltersColumnProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const states = Object.values(useStatesOptionsAbbr())
    .map((state) => state.display)
    .filter((state) => state !== 'All');

  const findEmployeeCountLabel = (value: number | string) => {
    const parsedValue =
      typeof value === 'string' ? Number.parseInt(value, 10) : value;

    return contactEmployeeCountRanges.find(
      (range) => range.value === parsedValue
    )?.label;
  };

  const handleSubmit = () => {
    if (searchParams.age) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_age,
      });
    }
    if (searchParams.state) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_state,
      });
    }
    if (searchParams.city) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_city,
      });
    }
    if (searchParams.country) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_country,
      });
    }
    if (searchParams.birthMonth) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_birth_month,
      });
    }
    if (searchParams.birthYear) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_birth_year,
      });
    }
    if (searchParams.deathMonth) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_death_month,
      });
    }
    if (searchParams.deathYear) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_death_year,
      });
    }
    if (searchParams.title) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_title,
      });
    }
    if (searchParams.level?.length > 0) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_level,
      });
    }
    if (searchParams.industry) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_industry,
      });
    }
    if (searchParams.company) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_company,
      });
    }
    if (searchParams.company_size_min || searchParams.company_size_max) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_company_size,
      });
    }
    onClose();
  };

  const handleSearchLocation = (propertyData: RapidsSearchData) => {
    const { city, state } = propertyData;
    const data = {
      city,
      state,
    };
    setSearchParams({ ...searchParams, ...data });
  };

  const handleStateSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchParams({ ...searchParams, state: value });
  };

  const getChipLabel = (filter: string) => {
    if (filter === 'birthMonth') {
      return 'Birth Month';
    }
    if (filter === 'deathMonth') {
      return 'Death Month';
    }
    if (filter === 'birthYear') {
      return 'Birth Year';
    }
    if (filter === 'deathYear') {
      return 'Death Year';
    }
    if (filter === 'company_size_min') {
      return 'Company Size Min';
    }
    if (filter === 'company_size_max') {
      return 'Company Size Max';
    }
    return nameize(filter);
  };

  const renderFilterChips = () => {
    const filterChips = FILTERS_BY_COLLECTION_TYPE[collectionType]
      .filter((filter) =>
        filter === 'level'
          ? searchParams[filter as keyof AdvancedSearchURLParams]?.length > 0
          : searchParams[filter as keyof AdvancedSearchURLParams]
      )
      .map((filter) => {
        if (filter === 'level') {
          return searchParams['level']?.map((level) => (
            <Chip
              key={level}
              label={`${getChipLabel(filter)}: ${nameize(level)}`}
              onDelete={() => {
                setSearchParams({
                  ...searchParams,
                  level: searchParams.level.filter((l: string) => l !== level),
                });
              }}
              sx={{
                m: 0.5,
              }}
            />
          ));
        }
        return (
          <Chip
            key={filter}
            label={`${getChipLabel(filter)}: ${
              filter === 'company_size_min' || filter === 'company_size_max'
                ? findEmployeeCountLabel(
                    searchParams[filter as keyof AdvancedSearchURLParams]
                  )
                : searchParams[filter as keyof AdvancedSearchURLParams]
            }`}
            onDelete={() => {
              setSearchParams({
                ...searchParams,
                [filter as keyof AdvancedSearchURLParams]: '',
              });
            }}
            sx={{
              m: 0.5,
            }}
          />
        );
      });
    if (filterChips.length > 0) {
      return (
        <Stack direction="row" flexWrap="wrap">
          {filterChips}
        </Stack>
      );
    }
    return null;
  };

  const handleMonthSelect = (value: string, type: 'birth' | 'death') => {
    setSearchParams({
      ...searchParams,
      [type === 'birth' ? 'birthMonth' : 'deathMonth']: value,
    });
  };

  return (
    <ReportColumnContainer
      spacing={4}
      minWidth={currentWidth}
      width="30%"
      height="98%"
      sx={{
        p: 2,

        ...(isMobileBreakpoint && {
          animation: 'fadeInScale 0.4s ease-out',
          '@keyframes fadeInScale': {
            '0%': {
              transform: 'scale(0.95)',
              opacity: 0,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Tune />
          <Text variant="body1" fontWeight="bold" color="text.primary">
            Filters
          </Text>
        </Box>
        <Close
          onClick={onClose}
          sx={{ color: 'text.secondary', cursor: 'pointer' }}
        />
      </Stack>
      <Text color="text.secondary">
        Search for possible personal details, such as addresses, phone numbers,
        and associates
      </Text>
      <SourceSelect
        collectionType={collectionType}
        setCollectionType={setCollectionType}
      />
      {renderFilterChips()}
      <Stack spacing={3} flexGrow={1}>
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.fullName}
          label="Full Name"
          data-cy="full_name_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, fullName: e.target.value })
          }
          variant="outlined"
          disabled={true}
        />
        <Stack direction="row" spacing={2}>
          <RapidsInput
            parentFormSubmitHandler={handleSearchLocation}
            searchTypes={['city']}
            includeCounties={false}
            updateEmptyOptions
            valueOveride={searchParams.city}
            inputOptions={{
              placeholder: 'City',
              hideSearchButton: true,
              hideInputLabel: true,
              InputProps: {
                sx: {
                  borderRadius: '4px',
                },
              },
              autoFocus: false,
              customStyles: RapidsInputStyle,
            }}
            disableCloseOnSelect={true}
            data-cy="city_field"
            inputLabel="City"
          />
          <Autocomplete
            id="state"
            data-cy="state_header_field"
            options={states}
            value={searchParams.state}
            renderInput={(params) => (
              <Input
                {...params}
                label="State"
                variant="outlined"
                name="state"
                data-cy="state_report_field"
                onInput={handleStateSelect}
                onChange={handleStateSelect}
              />
            )}
            onChange={(_event, data) =>
              setSearchParams({ ...searchParams, state: data })
            }
            isOptionEqualToValue={(option, optionValue) =>
              optionValue === '' || option === optionValue
            }
          />
        </Stack>
        {collectionType === CollectionType.FAMILY_SEARCH && (
          <Input
            fullWidth
            autoFocus={false}
            value={searchParams.country}
            label="Country"
            data-cy="country_field"
            slotProps={{
              input: {
                sx: {
                  borderRadius: '4px',
                },
              },
            }}
            onChange={(e) =>
              setSearchParams({ ...searchParams, country: e.target.value })
            }
            variant="outlined"
          />
        )}
        {collectionType === CollectionType.PUBLIC_RECORDS && (
          <AgeInput
            currentAge={
              searchParams.age ? Number.parseInt(searchParams.age, 10) : 0
            }
            onChange={(value) =>
              setSearchParams({ ...searchParams, age: value.toString() })
            }
          />
        )}
        {(collectionType === CollectionType.OBITUARIES ||
          collectionType === CollectionType.FAMILY_SEARCH) && (
          <>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ width: '50%' }}>
                <InputLabel
                  id="birthMonth"
                  shrink={Boolean(searchParams.birthMonth)}
                  sx={{ top: searchParams.birthMonth ? '0px' : '-7px' }}
                >
                  Birth Month
                </InputLabel>
                <Select
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline legend': {
                      width: searchParams.birthMonth ? 'auto' : '0px',
                      overflow: 'hidden',
                    },
                  }}
                  value={searchParams.birthMonth}
                  options={monthOptions}
                  id="birthMonth"
                  data-cy="birth_month_select"
                  MenuProps={{
                    disablePortal: true,
                  }}
                  label="Birth Month"
                  labelId="birthMonth"
                  onOptionSelect={(value) => handleMonthSelect(value, 'birth')}
                />
              </FormControl>
              <Input
                placeholder="Year"
                sx={{ width: '50%' }}
                type="number"
                label="Year"
                value={searchParams.birthYear}
                onChange={(e) =>
                  setSearchParams({
                    ...searchParams,
                    birthYear: e?.target?.value || '',
                  })
                }
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ width: '50%' }}>
                <InputLabel
                  id="deathMonth"
                  shrink={Boolean(searchParams.deathMonth)}
                  sx={{ top: searchParams.deathMonth ? '0px' : '-7px' }}
                >
                  Death Month
                </InputLabel>
                <Select
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline legend': {
                      width: searchParams.deathMonth ? 'auto' : '0px',
                      overflow: 'hidden',
                    },
                  }}
                  value={searchParams.deathMonth}
                  options={monthOptions}
                  id="deathMonth"
                  data-cy="death_month_select"
                  MenuProps={{
                    disablePortal: true,
                  }}
                  label="Death Month"
                  labelId="deathMonth"
                  onOptionSelect={(value) => handleMonthSelect(value, 'death')}
                />
              </FormControl>
              <Input
                placeholder="Year"
                sx={{ width: '50%' }}
                type="number"
                label="Year"
                value={searchParams.deathYear}
                onChange={(e) =>
                  setSearchParams({
                    ...searchParams,
                    deathYear: e?.target?.value || '',
                  })
                }
              />
            </Stack>
          </>
        )}
        {collectionType === CollectionType.CONTACTS && (
          <>
            <Text variant="body1" fontWeight="bold" color="text.primary">
              Professional Search
            </Text>
            <Input
              fullWidth
              autoFocus={false}
              value={searchParams.title}
              label="Job Title"
              data-cy="title_field"
              slotProps={{
                input: {
                  sx: {
                    borderRadius: '4px',
                  },
                },
              }}
              onChange={(e) =>
                setSearchParams({ ...searchParams, title: e.target.value })
              }
              variant="outlined"
            />
            <Input
              fullWidth
              autoFocus={false}
              value={searchParams.company}
              label="Company"
              data-cy="company_field"
              slotProps={{
                input: {
                  sx: {
                    borderRadius: '4px',
                  },
                },
              }}
              onChange={(e) =>
                setSearchParams({ ...searchParams, company: e.target.value })
              }
              variant="outlined"
            />
            <Autocomplete
              fullWidth
              autoFocus={false}
              value={searchParams.industry}
              options={contactIndustries}
              data-cy="industry_field"
              renderInput={(params) => (
                <Input {...params} label="Industry" variant="outlined" />
              )}
              onChange={(_event, data) =>
                setSearchParams({ ...searchParams, industry: data })
              }
              isOptionEqualToValue={(option, optionValue) =>
                optionValue === '' || option === optionValue
              }
            />
            <Text variant="body1" fontWeight="bold" color="text.primary">
              Seniority
            </Text>
            <Stack>
              {contactSeniorityLevels.map((level) => (
                <FormControlLabel
                  key={level.value}
                  control={
                    <Checkbox
                      checked={
                        searchParams.level?.includes(level.value) ?? false
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newLevels = [
                            ...(searchParams.level || []),
                            level.value,
                          ];
                          setSearchParams({
                            ...searchParams,
                            level: newLevels,
                          });
                        } else {
                          const newLevels = (searchParams.level || []).filter(
                            (l: string) => l !== level.value
                          );
                          setSearchParams({
                            ...searchParams,
                            level: newLevels,
                          });
                        }
                      }}
                    />
                  }
                  label={level.label}
                />
              ))}
            </Stack>
            <Text variant="body1" fontWeight="bold" color="text.primary">
              Employee Count
            </Text>
            <Stack>
              <Slider
                getAriaLabel={(index) =>
                  index === 0 ? 'Min Employee Count' : 'Max Employee Count'
                }
                name="employee-count"
                data-cy="employee_count_slider"
                min={1}
                max={9}
                value={[
                  searchParams.company_size_min
                    ? Number.parseInt(searchParams.company_size_min, 10)
                    : 1,
                  searchParams.company_size_max
                    ? Number.parseInt(searchParams.company_size_max, 10)
                    : 9,
                ]}
                valueLabelDisplay="auto"
                marks={contactEmployeeCountRanges.map((range) => ({
                  value: range.value,
                }))}
                step={null}
                onChange={(_, value) => {
                  if (Array.isArray(value)) {
                    setSearchParams({
                      ...searchParams,
                      company_size_min: value[0]?.toString() ?? '',
                      company_size_max: value[1]?.toString() ?? '',
                    });
                  }
                }}
                valueLabelFormat={(value: number) =>
                  findEmployeeCountLabel(value)
                }
              />
              <Text
                variant="caption"
                sx={{ textAlign: 'center' }}
                data-cy="employee_count_label"
              >
                Min{' '}
                {findEmployeeCountLabel(searchParams.company_size_min) ?? '1'} -
                Max{' '}
                {findEmployeeCountLabel(searchParams.company_size_max) ??
                  '10000+'}
              </Text>
            </Stack>
          </>
        )}
      </Stack>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Show Results
        </Button>
      </Box>
    </ReportColumnContainer>
  );
};
